import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

function AdminAssessments() {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [formData, setFormData] = useState({
    program: '',
    sectionName: '',
    sectionOrder: 1,
    title: '',
    description: '',
    type: 'section',
    duration: 30,
    passingScore: 60,
    questions: [],
    isActive: true
  });
  const [questionInput, setQuestionInput] = useState({
    question: '',
    type: 'mcq',
    options: ['', '', '', ''],
    correctAnswer: '',
    points: 10
  });

  useEffect(() => {
    fetchAssessments();
    fetchPrograms();
  }, []);

  const fetchAssessments = async () => {
    try {
      const res = await api.get('/admin/assessments');
      setAssessments(res.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const res = await api.get('/admin/programs');
      setPrograms(res.data);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingAssessment) {
        await api.put(`/admin/assessments/${editingAssessment._id}`, formData);
      } else {
        await api.post('/admin/assessments', formData);
      }

      setShowModal(false);
      resetForm();
      fetchAssessments();
    } catch (error) {
      alert(error.response?.data?.message || 'Error saving assessment');
    }
  };

  const handleEdit = (assessment) => {
    setEditingAssessment(assessment);
    setFormData({
      program: assessment.program._id || assessment.program,
      sectionName: assessment.sectionName,
      sectionOrder: assessment.sectionOrder,
      title: assessment.title,
      description: assessment.description,
      type: assessment.type,
      duration: assessment.duration,
      passingScore: assessment.passingScore,
      questions: assessment.questions || [],
      isActive: assessment.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this assessment?')) return;
    
    try {
      await api.delete(`/admin/assessments/${id}`);
      fetchAssessments();
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting assessment');
    }
  };

  const addQuestion = () => {
    if (!questionInput.question.trim()) return;
    
    const newQuestion = { ...questionInput };
    
    // Filter empty options
    if (newQuestion.type === 'mcq') {
      newQuestion.options = newQuestion.options.filter(o => o.trim());
    } else {
      delete newQuestion.options;
    }
    
    setFormData({
      ...formData,
      questions: [...formData.questions, newQuestion]
    });
    
    setQuestionInput({
      question: '',
      type: 'mcq',
      options: ['', '', '', ''],
      correctAnswer: '',
      points: 10
    });
  };

  const removeQuestion = (index) => {
    setFormData({
      ...formData,
      questions: formData.questions.filter((_, i) => i !== index)
    });
  };

  const updateOptionValue = (index, value) => {
    const newOptions = [...questionInput.options];
    newOptions[index] = value;
    setQuestionInput({...questionInput, options: newOptions});
  };

  const resetForm = () => {
    setEditingAssessment(null);
    setFormData({
      program: '',
      sectionName: '',
      sectionOrder: 1,
      title: '',
      description: '',
      type: 'section',
      duration: 30,
      passingScore: 60,
      questions: [],
      isActive: true
    });
    setQuestionInput({
      question: '',
      type: 'mcq',
      options: ['', '', '', ''],
      correctAnswer: '',
      points: 10
    });
  };

  return (
    <div className="container-fluid mt-4">
      <div className="row mb-3">
        <div className="col">
          <h2>Assessment Management</h2>
        </div>
        <div className="col-auto">
          <button 
            className="btn btn-primary"
            onClick={() => { resetForm(); setShowModal(true); }}
          >
            + Add Assessment
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center"><div className="spinner-border"></div></div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th>Title</th>
                <th>Program</th>
                <th>Section</th>
                <th>Type</th>
                <th>Questions</th>
                <th>Duration</th>
                <th>Pass %</th>
                <th>Stats</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assessments.map((assessment) => (
                <tr key={assessment._id}>
                  <td><strong>{assessment.title}</strong></td>
                  <td>{assessment.program?.title}</td>
                  <td>Section {assessment.sectionOrder}</td>
                  <td><span className="badge bg-info">{assessment.type}</span></td>
                  <td>{assessment.questions?.length || 0}</td>
                  <td>{assessment.duration} min</td>
                  <td>{assessment.passingScore}%</td>
                  <td>
                    <small>
                      {assessment.stats?.totalSubmissions || 0} attempts<br/>
                      Pass rate: {assessment.stats?.passRate || 0}%
                    </small>
                  </td>
                  <td>
                    <span className={`badge bg-${assessment.isActive ? 'success' : 'secondary'}`}>
                      {assessment.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="btn btn-sm btn-warning me-1"
                      onClick={() => handleEdit(assessment)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(assessment._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-xl" style={{maxWidth: '90%'}}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingAssessment ? 'Edit' : 'Add'} Assessment</h5>
                <button className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body" style={{maxHeight: '70vh', overflowY: 'auto'}}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Program *</label>
                      <select 
                        className="form-select"
                        value={formData.program}
                        onChange={(e) => setFormData({...formData, program: e.target.value})}
                        required
                      >
                        <option value="">Select Program</option>
                        {programs.map(p => (
                          <option key={p._id} value={p._id}>{p.title}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Type</label>
                      <select 
                        className="form-select"
                        value={formData.type}
                        onChange={(e) => setFormData({...formData, type: e.target.value})}
                      >
                        <option value="section">Section</option>
                        <option value="screening">Screening</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Section Name *</label>
                      <input 
                        type="text" 
                        className="form-control"
                        value={formData.sectionName}
                        onChange={(e) => setFormData({...formData, sectionName: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Section Order *</label>
                      <input 
                        type="number" 
                        className="form-control"
                        value={formData.sectionOrder}
                        onChange={(e) => setFormData({...formData, sectionOrder: parseInt(e.target.value)})}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Title *</label>
                    <input 
                      type="text" 
                      className="form-control"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea 
                      className="form-control" 
                      rows="2"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                    ></textarea>
                  </div>

                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Duration (minutes) *</label>
                      <input 
                        type="number" 
                        className="form-control"
                        value={formData.duration}
                        onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
                        required
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Passing Score (%) *</label>
                      <input 
                        type="number" 
                        className="form-control"
                        value={formData.passingScore}
                        onChange={(e) => setFormData({...formData, passingScore: parseInt(e.target.value)})}
                        min="0"
                        max="100"
                        required
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Status</label>
                      <select 
                        className="form-select"
                        value={formData.isActive}
                        onChange={(e) => setFormData({...formData, isActive: e.target.value === 'true'})}
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <hr />
                  <h6>Questions ({formData.questions.length})</h6>
                  
                  {/* Question Input Form */}
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="mb-2">
                        <label className="form-label">Question Text</label>
                        <input 
                          type="text" 
                          className="form-control"
                          value={questionInput.question}
                          onChange={(e) => setQuestionInput({...questionInput, question: e.target.value})}
                          placeholder="Enter question text"
                        />
                      </div>
                      <div className="row">
                        <div className="col-md-4 mb-2">
                          <label className="form-label">Type</label>
                          <select 
                            className="form-select form-select-sm"
                            value={questionInput.type}
                            onChange={(e) => setQuestionInput({...questionInput, type: e.target.value})}
                          >
                            <option value="mcq">Multiple Choice</option>
                            <option value="fill-blank">Fill in the Blank</option>
                          </select>
                        </div>
                        <div className="col-md-4 mb-2">
                          <label className="form-label">Points</label>
                          <input 
                            type="number" 
                            className="form-control form-control-sm"
                            value={questionInput.points}
                            onChange={(e) => setQuestionInput({...questionInput, points: parseInt(e.target.value)})}
                          />
                        </div>
                        <div className="col-md-4 mb-2">
                          <label className="form-label">Correct Answer</label>
                          <input 
                            type="text" 
                            className="form-control form-control-sm"
                            value={questionInput.correctAnswer}
                            onChange={(e) => setQuestionInput({...questionInput, correctAnswer: e.target.value})}
                            placeholder={questionInput.type === 'mcq' ? 'Exact option text' : 'Answer'}
                          />
                        </div>
                      </div>
                      {questionInput.type === 'mcq' && (
                        <div className="mb-2">
                          <label className="form-label">Options</label>
                          {questionInput.options.map((opt, i) => (
                            <input 
                              key={i}
                              type="text" 
                              className="form-control form-control-sm mb-1"
                              value={opt}
                              onChange={(e) => updateOptionValue(i, e.target.value)}
                              placeholder={`Option ${i + 1}`}
                            />
                          ))}
                        </div>
                      )}
                      <button type="button" className="btn btn-sm btn-success" onClick={addQuestion}>
                        + Add Question
                      </button>
                    </div>
                  </div>

                  {/* Questions List */}
                  {formData.questions.map((q, index) => (
                    <div key={index} className="card mb-2">
                      <div className="card-body p-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <strong>Q{index + 1}:</strong> {q.question}
                            <div className="text-muted small">
                              Type: {q.type} | Points: {q.points} | Answer: {q.correctAnswer}
                            </div>
                            {q.type === 'mcq' && q.options && (
                              <div className="text-muted small">
                                Options: {q.options.join(', ')}
                              </div>
                            )}
                          </div>
                          <button 
                            type="button" 
                            className="btn btn-sm btn-danger"
                            onClick={() => removeQuestion(index)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingAssessment ? 'Update' : 'Create'} Assessment
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAssessments;
